import { computed, Injectable, signal } from '@angular/core';
import {
  DEFAULT_FILTER_STATE,
  FilterSpec,
  FilterState,
  SortDirection,
} from '../models/filter.model';
import { todayISO } from '../utils/date-format';
import { STORAGE_KEYS } from '../constants/storage-keys';

@Injectable({ providedIn: 'root' })
export class TableStateService {
  readonly state = signal<FilterState>(this.loadFromStorage());

  readonly filters = computed(() => this.state().rules);
  readonly sort = computed(() => this.state().sort);
  readonly page = computed(() => this.state().page);
  readonly pageSize = computed(() => this.state().pageSize);
  readonly visibleColumns = computed(() => this.state().visibleColumns);
  readonly snapshotDate = computed(() => this.state().snapshotDate);

  setFilters(rules: FilterSpec[]): void {
    this.update({ rules, page: 1 });
  }

  setSort(field: string, direction: SortDirection): void {
    this.update({ sort: [{ field, direction }] });
  }

  toggleSort(field: string): void {
    const current = this.state().sort.find((s) => s.field === field);

    if (!current) {
      this.setSort(field, 'asc');
    } else if (current.direction === 'asc') {
      this.setSort(field, 'desc');
    } else {
      this.update({ sort: this.state().sort.filter((s) => s.field !== field) });
    }
  }

  // UI делает single-sort, но контракт с бэком — массив. Метод возвращает
  // direction только для активной (единственной) сортировки. Если когда-нибудь
  // включим multi-sort — будет показывать первое совпадение, что соответствует
  // приоритету.
  getSortDirection(field: string): SortDirection | null {
    return this.state().sort.find((s) => s.field === field)?.direction ?? null;
  }

  setSnapshotDate(snapshotDate: string | null): void {
    this.update({ snapshotDate, page: 1 });
  }

  resetFiltersAndSort(): void {
    // Видимость колонок НЕ сбрасываем — по ТЗ 3.2 она должна жить между сессиями.
    this.update({
      rules: [],
      sort: [],
      page: 1,
      snapshotDate: todayISO(),
    });
  }

  setVisibleColumns(columns: string[]): void {
    this.update({ visibleColumns: columns });
  }

  private update(partial: Partial<FilterState>): void {
    const next = { ...this.state(), ...partial };
    this.state.set(next);
    this.saveToStorage(next);
  }

  private saveToStorage(state: FilterState): void {
    // visibleColumns идёт в localStorage (переживает сессию, ТЗ 3.2),
    // остальное — в sessionStorage (фильтры/срез обнуляются при новом логине).
    try {
      const { visibleColumns, ...rest } = state;
      sessionStorage.setItem(STORAGE_KEYS.tableFilterState, JSON.stringify(rest));
      localStorage.setItem(STORAGE_KEYS.tableVisibleColumns, JSON.stringify(visibleColumns));
    } catch (e) {
      console.warn('[table-state] Не удалось сохранить состояние таблицы:', e);
    }
  }

  private loadFromStorage(): FilterState {
    let sessionPart: Partial<FilterState> = {};
    let visibleColumns: string[] = [];
    try {
      const raw = sessionStorage.getItem(STORAGE_KEYS.tableFilterState);
      if (raw) {
        const parsed = JSON.parse(raw) as unknown;
        sessionPart = isValidSessionShape(parsed) ? parsed : {};
        if (!isValidSessionShape(parsed)) {
          sessionStorage.removeItem(STORAGE_KEYS.tableFilterState);
        }
      }
    } catch (e) {
      console.warn('[table-state] Битый tableFilterState — стираем:', e);
      sessionStorage.removeItem(STORAGE_KEYS.tableFilterState);
    }
    try {
      const rawCols = localStorage.getItem(STORAGE_KEYS.tableVisibleColumns);
      if (rawCols) {
        const parsed = JSON.parse(rawCols) as unknown;
        visibleColumns =
          Array.isArray(parsed) && parsed.every((v) => typeof v === 'string')
            ? (parsed as string[])
            : [];
      }
    } catch (e) {
      console.warn('[table-state] Битый tableVisibleColumns — стираем:', e);
      localStorage.removeItem(STORAGE_KEYS.tableVisibleColumns);
    }
    return {
      ...DEFAULT_FILTER_STATE,
      ...sessionPart,
      visibleColumns,
      snapshotDate: sessionPart.snapshotDate ?? todayISO(),
    };
  }
}

function isValidSessionShape(value: unknown): value is Partial<FilterState> {
  if (!value || typeof value !== 'object') return false;
  const obj = value as Record<string, unknown>;
  if ('rules' in obj && !Array.isArray(obj['rules'])) return false;
  if ('sort' in obj && !Array.isArray(obj['sort'])) return false;
  if ('snapshotDate' in obj && obj['snapshotDate'] !== null && typeof obj['snapshotDate'] !== 'string')
    return false;
  if ('page' in obj && typeof obj['page'] !== 'number') return false;
  if ('pageSize' in obj && typeof obj['pageSize'] !== 'number') return false;
  return true;
}
